import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelRadio as _vModelRadio, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createVNode as _createVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, withCtx as _withCtx, vShow as _vShow, withKeys as _withKeys, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-07c66df4"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "name-search-page" }
const _hoisted_2 = { class: "with-subhead" }
const _hoisted_3 = { class: "subhead" }
const _hoisted_4 = {
  class: "btn-group scope-select",
  role: "group",
  "aria-label": "Scope"
}
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  class: "btn btn-outline-primary btn-sm",
  for: "scope-ancestors"
}, "My family", -1))
const _hoisted_6 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("label", {
  class: "btn btn-outline-primary btn-sm",
  for: "scope-entire"
}, "Entire tree", -1))
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { class: "timeline-chart" }
const _hoisted_9 = { class: "gridlines" }
const _hoisted_10 = { class: "year-label" }
const _hoisted_11 = { class: "list-unstyled person-list" }
const _hoisted_12 = ["role", "tabindex", "aria-expanded", "aria-label", "onClick", "onKeydown"]
const _hoisted_13 = { class: "person-info" }
const _hoisted_14 = { class: "person-name-line" }
const _hoisted_15 = { class: "person-name" }
const _hoisted_16 = {
  key: 0,
  class: "relation-path"
}
const _hoisted_17 = { class: "lifespan-icons" }
const _hoisted_18 = {
  key: 0,
  class: "icon-label death-end"
}
const _hoisted_19 = ["src"]
const _hoisted_20 = { class: "icon-label birth-end" }
const _hoisted_21 = ["src"]

import { computed, Ref, ref, watch } from 'vue'
import { ArrowRightIcon } from '@zhuowenli/vue-feather-icons'
import LifespanBar from '../time/LifespanBar.vue'
import { usePlaceStore } from '@/rd/PlaceStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { useExploreStore } from '../ExploreStore'
import { useViewScopeStore } from '@/explore/ViewScopeStore'
import { LoadMode } from '@/util/AsyncData'
import ScopeLoadProgress from '@/explore/ScopeLoadProgress.vue'
import { getPersonNamePartDisplayText, PersonName, PersonNamePart, ViewPerson } from '@/rd/ResearchDataModel'
import { capitalize } from '@/util/LanguageUtil'
import { DurationUtil } from '@/util/LuxonUtil'
import { useRelationTextBuilder } from '@/rd/RelationPath'
import { DateTime } from 'luxon'

const defaultMinYear = 1700
const unknownLifespan = 80

type ScopeMode = 'ancestors' | 'entireTree'

export default /*#__PURE__*/_defineComponent({
  __name: 'NameSearchPage',
  props: {
    part: {},
    name: {}
  },
  setup(__props: any) {

const props = __props;



const maxYear = DateTime.now().year
const viewPersonStore = useViewPersonStore()
const placeStore = usePlaceStore()
const exploreStore = useExploreStore()
const viewScopeStore = useViewScopeStore()

const scopeMode = ref<ScopeMode>('ancestors')

const scopeInfoRef = computed(() =>
  viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.data)

const scopeLoading = computed(() =>
  scopeMode.value === 'ancestors' &&
  !viewScopeStore.getAsyncScopeInfo(exploreStore.startPersonId, LoadMode.EnsureLoaded)?.isLoaded)
const { getRelationText } = useRelationTextBuilder()

const sunriseIcon = new URL('@/assets/icons/sunrise_gray_60.png', import.meta.url).href
const sunsetIcon = new URL('@/assets/icons/sunset_gray_60.png', import.meta.url).href
const expandedId = ref<string | null>(null)

const personsRef = ref([]) as Ref<ViewPerson[]>
const partDisplayNameRef = computed(() => capitalize(getPersonNamePartDisplayText(props.part)))
const minYearRef = computed(() =>
  Math.floor(personsRef.value.reduce((min, vp) => Math.min(min, vp.birthYear ?? min), defaultMinYear) / 100) * 100)

const gridYears = computed(() => {
  const start = Math.ceil(minYearRef.value / 50) * 50
  const years: number[] = []
  for (let y = start; y <= maxYear; y += 50) {
    years.push(y)
  }
  return years
})

watch([props, scopeMode, scopeInfoRef], async () => {
  const nameKey = props.name.toLowerCase()

  let viewPersons: ViewPerson[]

  if (scopeMode.value === 'ancestors') {
    if (!scopeInfoRef.value) {
      personsRef.value = []
      return
    }
    viewPersons = []
    for (const id of scopeInfoRef.value.personIds) {
      const vp = viewPersonStore.getLoadedPerson(id)
      if (vp) viewPersons.push(vp)
    }
  } else {
    viewPersons = (await viewPersonStore.searchByNamesAsync([nameKey]))
      .map(a => a.data!)
  }

  personsRef.value = viewPersons
    .filter(p => new PersonName(p.displayProperties.name).getPart(props.part)?.toLowerCase().includes(nameKey))
    .sort((a, b) => {
      const ay = a.birthYear, by = b.birthYear
      if (ay == null && by == null) return 0
      if (ay == null) return -1
      if (by == null) return 1
      return by - ay // desc
    })
}, { immediate: true })

watch([personsRef, () => exploreStore.startPersonId], async () => {
  if (exploreStore.startPersonId) {
    await viewPersonStore.ensureRelationPathsLoadedAsync(exploreStore.startPersonId, personsRef.value.map(p => p.id!))
  }
}, { immediate: true })

function toggleExpand(vp: ViewPerson) {
  expandedId.value = expandedId.value === vp.id ? null : vp.id!
}

function getAgeAtDeath(vp: ViewPerson) {
  const dp = vp.displayProperties
  const birth = dp.birthSortDate.date1
  const death = dp.deathSortDate.date1
  if (birth && !birth.isEmpty && death && !death.isEmpty) {
    return DurationUtil.toAge(death.diff(birth), true)
  }
}

function getBirthPlaceFlag(vp: ViewPerson) {
  return getBirthPlace(vp)?.countryCode?.toLowerCase()
}

function getBirthPlace(vp: ViewPerson) {
  if (vp.displayProperties.birthPlaceId) {
    return placeStore.asyncPlaces.get(vp.displayProperties.birthPlaceId)?.data
  }
  return undefined
}

function getDisplayBirthYear(vp: ViewPerson) {
  return vp.birthYear ?? (vp.deathYear ? vp.deathYear - unknownLifespan : undefined)
}

function getDisplayDeathYear(vp: ViewPerson) {
  return vp.deathYear ?? (vp.birthYear ? vp.birthYear + unknownLifespan : undefined)
}

function yearToPercent(year: number) {
  // Present (maxYear) at 0% (left), past (minYear) at 100% (right)
  return ((maxYear - year) / (maxYear - minYearRef.value)) * 100
}

function getBarStyle(vp: ViewPerson) {
  const birthYear = getDisplayBirthYear(vp)
  const deathYear = getDisplayDeathYear(vp)
  if (birthYear != null && deathYear != null) {
    const left = yearToPercent(deathYear)
    const width = Math.max(yearToPercent(birthYear) - left, 1)
    return { left: left + '%', width: width + '%' }
  }
}

function getRelationPathText(vp: ViewPerson) {
  if (exploreStore.startPersonId) {
    const path = viewPersonStore.getLoadedRelationPath(exploreStore.startPersonId, vp.id!)
    if (path) {
      return getRelationText(path)
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("h1", _hoisted_2, _toDisplayString(_unref(capitalize)(props.name)), 1),
    _createElementVNode("p", _hoisted_3, _toDisplayString(partDisplayNameRef.value), 1),
    _createElementVNode("div", _hoisted_4, [
      _withDirectives(_createElementVNode("input", {
        type: "radio",
        class: "btn-check",
        name: "scope",
        id: "scope-ancestors",
        autocomplete: "off",
        value: "ancestors",
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((scopeMode).value = $event))
      }, null, 512), [
        [_vModelRadio, scopeMode.value]
      ]),
      _hoisted_5,
      _withDirectives(_createElementVNode("input", {
        type: "radio",
        class: "btn-check",
        name: "scope",
        id: "scope-entire",
        autocomplete: "off",
        value: "entireTree",
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((scopeMode).value = $event))
      }, null, 512), [
        [_vModelRadio, scopeMode.value]
      ]),
      _hoisted_6
    ]),
    (scopeLoading.value)
      ? (_openBlock(), _createBlock(ScopeLoadProgress, { key: 0 }))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (personsRef.value.length)
            ? (_openBlock(), _createElementBlock("h3", _hoisted_7, _toDisplayString(personsRef.value.length) + " people", 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_8, [
            _createElementVNode("div", _hoisted_9, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(gridYears.value, (year) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: year,
                  class: _normalizeClass(["gridline", { 'minor': year % 100 !== 0 }]),
                  style: _normalizeStyle({ left: yearToPercent(year) + '%' })
                }, [
                  _createElementVNode("span", _hoisted_10, _toDisplayString(year), 1)
                ], 6))
              }), 128))
            ]),
            _createElementVNode("ul", _hoisted_11, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(personsRef.value, (vp) => {
                return (_openBlock(), _createElementBlock("li", {
                  key: vp.id,
                  class: _normalizeClass(["person-row", { expanded: expandedId.value === vp.id, 'no-bar': !vp.birthYear }])
                }, [
                  _createElementVNode("div", {
                    class: "person-bar",
                    style: _normalizeStyle(getBarStyle(vp)),
                    role: vp.birthYear ? 'button' : undefined,
                    tabindex: vp.birthYear ? 0 : undefined,
                    "aria-expanded": vp.birthYear ? expandedId.value === vp.id : undefined,
                    "aria-label": vp.displayName,
                    onClick: ($event: any) => (vp.birthYear && toggleExpand(vp)),
                    onKeydown: [
                      _withKeys(_withModifiers(($event: any) => (vp.birthYear && toggleExpand(vp)), ["prevent"]), ["enter"]),
                      _withKeys(_withModifiers(($event: any) => (vp.birthYear && toggleExpand(vp)), ["prevent"]), ["space"])
                    ]
                  }, [
                    _createElementVNode("div", _hoisted_13, [
                      _createElementVNode("div", _hoisted_14, [
                        (getBirthPlaceFlag(vp))
                          ? (_openBlock(), _createElementBlock("div", {
                              key: 0,
                              class: _normalizeClass(["fi", 'fi-' + getBirthPlaceFlag(vp)])
                            }, null, 2))
                          : _createCommentVNode("", true),
                        _createElementVNode("span", _hoisted_15, _toDisplayString(vp.displayName), 1),
                        _createVNode(_component_router_link, {
                          to: vp.pageUrl!,
                          class: "person-link",
                          "aria-label": "View person",
                          onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(ArrowRightIcon))
                          ]),
                          _: 2
                        }, 1032, ["to"])
                      ]),
                      (getRelationPathText(vp))
                        ? (_openBlock(), _createElementBlock("div", _hoisted_16, _toDisplayString(getRelationPathText(vp)), 1))
                        : _createCommentVNode("", true)
                    ]),
                    (vp.birthYear)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                          _createVNode(LifespanBar, {
                            "birth-year": vp.birthYear,
                            "death-year": vp.deathYear,
                            age: getAgeAtDeath(vp),
                            living: vp.displayProperties?.living,
                            layout: expandedId.value === vp.id ? 'full' : 'small',
                            class: "lifespan-bar-instance"
                          }, null, 8, ["birth-year", "death-year", "age", "living", "layout"]),
                          _withDirectives(_createElementVNode("div", _hoisted_17, [
                            (vp.deathYear)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                  _createElementVNode("img", {
                                    src: _unref(sunsetIcon),
                                    alt: "death"
                                  }, null, 8, _hoisted_19)
                                ]))
                              : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_20, [
                              _createElementVNode("img", {
                                src: _unref(sunriseIcon),
                                alt: "birth"
                              }, null, 8, _hoisted_21)
                            ])
                          ], 512), [
                            [_vShow, expandedId.value === vp.id]
                          ])
                        ], 64))
                      : _createCommentVNode("", true)
                  ], 44, _hoisted_12)
                ], 2))
              }), 128))
            ])
          ])
        ], 64))
  ]))
}
}

})